import { format } from 'date-fns';
import { CURRENCY, DATE_FORMATS } from './constants';
import {PaymentMethod} from "@/services/types.ts";

/**
 * Format number as currency (Indonesian Rupiah)
 */
export function formatCurrency(amount: number | string): string {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount)) return `${CURRENCY.SYMBOL} 0`;

  return new Intl.NumberFormat(CURRENCY.LOCALE, {
    style: 'currency',
    currency: CURRENCY.CODE,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numAmount);
}

/**
 * Format number with thousand separators
 */
export function formatNumber(num: number | string): string {
  const numValue = typeof num === 'string' ? parseFloat(num) : num;
  
  if (isNaN(numValue)) return '0';

  return new Intl.NumberFormat(CURRENCY.LOCALE).format(numValue);
}

/**
 * Convert UTC string or Date to local Date object
 */
export function toLocalDate(date: string | Date): Date {
  if (date instanceof Date) return date;

  // 1. Check if the string already has a timezone indicator.
  // 2. If not, append 'Z' to force the browser to treat it as UTC.
  const utcString = date.endsWith("Z") || date.includes("+")
    ? date
    : `${date}Z`;

  return new Date(utcString);
}

/**
 * Format date
 */
export function formatDate(
  date: string | Date,
  formatString: string = DATE_FORMATS.DISPLAY
): string {
  try {
    const dateObj = toLocalDate(date);
    return format(dateObj, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

/**
 * Format date with time
 */
export function formatDateTime(date: string | Date): string {
  try {
    const dateObj = toLocalDate(date);
    return formatDate(dateObj, DATE_FORMATS.DISPLAY_WITH_TIME);
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return '';
  }
}

/**
 * Format time only
 */
export function formatTime(date: string | Date): string {
  try {
    const dateObj = toLocalDate(date);
    return formatDate(dateObj, DATE_FORMATS.TIME_ONLY);
  } catch (error) {
    console.error('Error formatting time:', error);
    return '';
  }
}

/**
 * Format phone number (add country code display)
 */
export function formatPhone(phone: string, countryCode: string = '+62'): string {
  if (!phone) return '';
  
  // Remove leading zero if exists
  const cleanPhone = phone.startsWith('0') ? phone.substring(1) : phone;
  
  // Format as: +62 812-3456-7890
  if (cleanPhone.length >= 10) {
    const parts = [
      cleanPhone.substring(0, 3),
      cleanPhone.substring(3, 7),
      cleanPhone.substring(7),
    ];
    return `${countryCode} ${parts.join('-')}`;
  }
  
  return `${countryCode} ${cleanPhone}`;
}

/**
 * Format barcode for display
 */
export function formatBarcode(barcode: string): string {
  if (!barcode) return '';
  
  // Format 13-digit EAN as: 899 1234 56789 0
  if (barcode.length === 13) {
    return `${barcode.substring(0, 3)} ${barcode.substring(3, 7)} ${barcode.substring(7, 12)} ${barcode.substring(12)}`;
  }
  
  return barcode;
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number = 50): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * Format file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Get initials from name
 */
export function getInitials(name: string): string {
  if (!name) return '';
  
  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Calculate percentage
 */
export function calculatePercentage(value: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((value / total) * 100);
}

/**
 * Format percentage
 */
export function formatPercentage(value: number, decimals: number = 0): string {
  return value.toFixed(decimals) + '%';
}

/**
 * Parse currency string to number
 */
export function parseCurrency(currencyString: string): number {
  // Remove currency symbol, spaces, and separators
  const cleaned = currencyString
    .replace(/Rp/g, '')
    .replace(/\s/g, '')
    .replace(/\./g, '')
    .replace(/,/g, '.');
  
  return parseFloat(cleaned) || 0;
}

/**
 * Capitalize first letter
 */
export function capitalize(text: string): string {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

/**
 * Convert to title case
 */
export function toTitleCase(text: string): string {
  if (!text) return '';
  return text
    .split(' ')
    .map(word => capitalize(word))
    .join(' ');
}

export function toPaymentMethod(paymentType: string): PaymentMethod {
  switch (paymentType) {
    case 'virtual_account' :
        return 'FLIPVA';
      break;
    case 'wallet_account':
        return 'FLIPEWALLET';
      break;
    default:
        return 'FLIPVA';
  }
}
/**
 * Compact relative time for cards and inbox rows: "12 mnt", "1 jam", "Kemarin, 17:40", else "24 Sep, 10:42".
 * `suffix` appends "lalu" / "ago" to the minute/hour forms.
 */
export function formatRelativeShort(date: string | Date, lang = 'id', suffix = false): string {
  const id = lang.startsWith('id');
  const d = new Date(date);
  const diffMin = Math.max(0, Math.round((Date.now() - d.getTime()) / 60_000));
  const time = d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
  const ago = (s: string) => (suffix ? `${s} ${id ? 'lalu' : 'ago'}` : s);
  if (diffMin < 1) return id ? 'Baru saja' : 'Just now';
  if (diffMin < 60) return ago(`${diffMin} ${id ? 'mnt' : 'min'}`);
  const now = new Date();
  const dayDiff = Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / 86_400_000);
  if (dayDiff === 0) return ago(`${Math.floor(diffMin / 60)} ${id ? 'jam' : 'h'}`);
  if (dayDiff === 1) return `${id ? 'Kemarin' : 'Yesterday'}, ${time}`;
  return `${d.toLocaleDateString(id ? 'id-ID' : 'en-US', { day: 'numeric', month: 'short' })}, ${time}`;
}
